import {
    CreateSocket,
    EVT_RECEIVE_BROADCAST_ASSIGN_END,
    EVT_RECEIVE_BROADCAST_ASSIGN_START,
    EVT_RECEIVE_BROADCAST_END_LESSON,
    EVT_RECEIVE_BROADCAST_LABEL,
    EVT_RECEIVE_BROADCAST_RECEIVE_ROOM_SETTINGS,
    EVT_RECEIVE_BROADCAST_STUDENT_CLIENT_IDX,
    EVT_RECEIVE_BROADCAST_USER_DISCONNECTED,
    EVT_REQUEST_TEACHER_ASSIGN_MULTIPLE_LABEL,
    EVT_REQUEST_TEACHER_ASSIGN_START,
    EVT_REQUEST_TEACHER_CLIENT_LIST,
    EVT_REQUEST_TEACHER_CONFIRM_ASSIGN,
    EVT_REQUEST_TEACHER_END_LESSON,
    EVT_REQUEST_TEACHER_UPDATE_ROOM_SETTINGS,
    EVT_REQUEST_USER_REQUEST_JOIN_ROOM,
    EVT_REQUEST_USER_ROOM_STATE,
} from '@/api/client/socket.io';
import { capture } from '@/handlers/error';
import { useSession } from '@/providers/session/SessionProvider';
import { Classroom } from '@/types/classroom';
import { ClassroomSettings, ClassroomState, ClientLabel, isClassroomSettings, isClientLabel, isRoomState } from '@/types/session';
import { StudentProfile } from '@/types/student';
import { formatName } from '@/utils/name';
import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { GET_STUDENT_PROFILE, GetStudentProfileResponse, GetStudentProfileVariables } from './gql';

const DEFAULT_TIMEOUT = 5000;

export type AssignAction = 'change' | 'delete';

export type AssignLabel = {
    studentName: string;
    socketId: string;
    studentProfileId: string;
    studentNumber: string;
    profilePicURL: string;
    action: AssignAction;
};

export type SocketConnectionStatus = 'disconnected' | 'connected' | 'reconnecting';

export const useClassroomSession = (classroom?: Classroom) => {
    const { session } = useSession();
    const [socket, setSocket] = useState<Socket | undefined>(undefined);
    const [clients, setClients] = useState<ClientLabel[]>([]);
    const [roomState, setRoomState] = useState<ClassroomState | undefined>(undefined);
    const [roomSettings, setRoomSettings] = useState<ClassroomSettings | undefined>(undefined);
    const [connectionStatus, setConnectionStatus] = useState<SocketConnectionStatus>('connected');
    const [getStudentProfile, { data }] = useLazyQuery<GetStudentProfileResponse, GetStudentProfileVariables>(GET_STUDENT_PROFILE, {
        variables: classroom?.class?.id
            ? {
                  classID: classroom?.class?.id,
              }
            : undefined,
    });

    const refreshClients = useCallback(async (soc: Socket, token: string) => {
        await soc
            .timeout(DEFAULT_TIMEOUT)
            .emitWithAck(EVT_REQUEST_TEACHER_CLIENT_LIST, { token })
            .then((clients: unknown) => {
                if (Array.isArray(clients) && clients.every(isClientLabel)) {
                    setClients(clients.filter((client) => client.clientNumber !== -1));
                }
            });
    }, []);

    const setDevices = useCallback(
        async (labels: AssignLabel[]) => {
            const data: { token: string; labels: AssignLabel[] } = {
                token: session?.token ?? '',
                labels: labels,
            };
            await socket?.timeout(DEFAULT_TIMEOUT).emitWithAck(EVT_REQUEST_TEACHER_ASSIGN_MULTIPLE_LABEL, data);
        },
        [session?.token, socket],
    );

    // allocate devices to students
    const allocate = useCallback(
        async (items: { profile: StudentProfile; clientId: string }[]) => {
            setDevices(
                items.map((item) => ({
                    studentName: formatName(item.profile.familyName, item.profile.givenName),
                    socketId: item.clientId,
                    studentProfileId: item.profile.id,
                    studentNumber: item.profile.studentNumber,
                    profilePicURL: item.profile.profilePic?.url || '',
                    action: 'change',
                })),
            );
        },
        [setDevices],
    );

    // deallocate devices from students
    const deallocate = useCallback(
        async (clientIds: string[]) => {
            setDevices(
                clientIds.map((clientId) => ({
                    studentName: '',
                    socketId: clientId,
                    studentProfileId: '',
                    studentNumber: '',
                    profilePicURL: '',
                    action: 'delete',
                })),
            );
        },
        [setDevices],
    );

    // trigger device reallocation
    const reallocate = useCallback(() => {
        socket?.emit(EVT_REQUEST_TEACHER_ASSIGN_START, { token: session?.token ?? '' });
    }, [session?.token, socket]);

    // starts the classroom
    const start = useCallback(() => {
        socket?.emit(EVT_REQUEST_TEACHER_CONFIRM_ASSIGN, { token: session?.token });
    }, [session?.token, socket]);

    // ends the classroom
    const terminate = useCallback(async () => {
        await socket?.emitWithAck(EVT_REQUEST_TEACHER_END_LESSON, { token: session?.token });
    }, [session?.token, socket]);

    // updates the classroom settings
    const setSettings = useCallback(
        async (settings: ClassroomSettings) => {
            await socket
                ?.timeout(DEFAULT_TIMEOUT)
                .emitWithAck(EVT_REQUEST_TEACHER_UPDATE_ROOM_SETTINGS, {
                    token: session?.token,
                    roomId: classroom?.id,
                    roomSettings: settings,
                    forceUpdate: true,
                })
                .then((settings: unknown) => {
                    console.log('[useClassroomSession] room setting:', settings);
                    if (isClassroomSettings(settings)) {
                        setRoomSettings(settings);
                    }
                });
        },
        [classroom, session?.token, socket],
    );

    useEffect(() => {
        if (classroom?.class?.id) {
            getStudentProfile();
        }
    }, [classroom?.class?.id, getStudentProfile]);

    useEffect(() => {
        if (!classroom) {
            return;
        }

        // check the server time to see if the classroom has ended
        const serverTime = classroom?.serverTime;
        const endTime = classroom?.endTime;
        if (serverTime && endTime && new Date(serverTime) >= new Date(endTime)) {
            setRoomState(ClassroomState.ENDED);
            return;
        }

        // socket connection
        let socket: Socket | null = null;
        if (session?.token) {
            CreateSocket(session.token)
                .then((soc) => {
                    socket = soc;

                    /**
                     * Set up socket event listeners for all broadcast events
                     */

                    // when a device (teacher/student) joins classroom
                    socket.on(EVT_RECEIVE_BROADCAST_STUDENT_CLIENT_IDX, (data) => {
                        if (isClientLabel(data)) {
                            if (data.socketId === soc.id) {
                                // joined classroom (teacher themself), init everything
                                console.log('[useClassroomSession] joined classroom:', classroom.id);

                                // init room state
                                soc.timeout(DEFAULT_TIMEOUT)
                                    .emitWithAck(EVT_REQUEST_USER_ROOM_STATE, null)
                                    .then((state: unknown) => {
                                        console.log('[useClassroomSession] room state:', state);
                                        if (isRoomState(state)) {
                                            setRoomState(state);
                                        }
                                    });

                                // init room settings
                                soc.timeout(DEFAULT_TIMEOUT)
                                    .emitWithAck(EVT_REQUEST_TEACHER_UPDATE_ROOM_SETTINGS, {
                                        token: session.token,
                                        roomId: classroom.id,
                                        roomSettings: {
                                            page: 0,
                                            screenSharing: false,
                                            writingHints: true,
                                            freeStyleMode: false,
                                            screenSync: true,
                                        },
                                        forceUpdate: false,
                                    })
                                    .then((settings: unknown) => {
                                        console.log('[useClassroomSession] room setting:', settings);
                                        if (isClassroomSettings(settings)) {
                                            setRoomSettings(settings);
                                        }
                                    });

                                // init clients
                                refreshClients(soc, session.token);
                            } else {
                                setClients((prev) => {
                                    if (data.clientNumber === -1) {
                                        return prev;
                                    }

                                    return [...prev, data];
                                });
                            }
                        }
                    });

                    // when a teacher click on the reassign button
                    socket.on(EVT_RECEIVE_BROADCAST_ASSIGN_START, () => {
                        setRoomState(ClassroomState.ASSIGNING);
                    });

                    // when a classroom is started by teacher
                    socket.on(EVT_RECEIVE_BROADCAST_ASSIGN_END, () => {
                        setRoomState(ClassroomState.LESSONING);
                    });

                    // when a classroom is ended by teacher
                    socket.on(EVT_RECEIVE_BROADCAST_END_LESSON, () => {
                        setRoomState(ClassroomState.ENDED);
                    });

                    // when the classroom's setting is changed by teacher
                    socket.on(EVT_RECEIVE_BROADCAST_RECEIVE_ROOM_SETTINGS, (settings) => {
                        if (isClassroomSettings(settings)) {
                            setRoomSettings(settings);
                        }
                    });

                    // when a student is assigned to a device by teacher
                    socket.on(EVT_RECEIVE_BROADCAST_LABEL, (client) => {
                        if (isClientLabel(client)) {
                            setClients((prev) => {
                                return prev.map((prevClient) => {
                                    if (client.socketId === prevClient.socketId) {
                                        return { ...client };
                                    }
                                    return prevClient;
                                });
                            });
                        }
                    });

                    // when a device is disconnected
                    socket.on(EVT_RECEIVE_BROADCAST_USER_DISCONNECTED, (client) => {
                        if (isClientLabel(client)) {
                            setClients((prev) => {
                                return prev.filter((prevClient) => {
                                    return prevClient.socketId !== client.socketId;
                                });
                            });
                        }
                    });

                    socket.on('connect', () => {
                        if (socket) {
                            console.log('[useClassroomSession] socket reconnected');
                            setConnectionStatus('connected');
                            // re-join the classroom because the socket id has changed
                            socket.emit(EVT_REQUEST_USER_REQUEST_JOIN_ROOM, { room: classroom.id, token: session.token });
                        }
                    });

                    // Handle socket reconnection after internet connectivity issues
                    socket.on('disconnect', (reason) => {
                        console.log('[useClassroomSession] socket disconnected:', reason);
                        if (socket && !socket.active) {
                            setConnectionStatus('disconnected');
                            capture(new Error('server disconnected the socket, requires manual reconnect, reason: ' + reason));
                        } else {
                            setConnectionStatus('reconnecting');
                        }
                    });

                    socket.emit(EVT_REQUEST_USER_REQUEST_JOIN_ROOM, { room: classroom.id, token: session.token });

                    setSocket(socket);
                })
                .catch((err) => {
                    console.error('[useClassroomSession] reconnecting socket, socket failed reason:', err);
                });
        }

        return () => {
            if (socket) {
                console.log('[useClassroomSession] disconnecting socket:', socket.id);
                socket.removeAllListeners();
                socket.disconnect();
            }
        };
    }, [classroom, refreshClients, session]);

    return {
        state: roomState,
        settings: roomSettings,
        clients,
        students: data?.studentProfiles,
        reallocate,
        allocate,
        deallocate,
        start,
        terminate,
        setSettings,
        connectionStatus,
    };
};
